import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePlanner } from '../context/PlannerContext';
import { generateWeeklySchedule, generateNextSteps } from '../utils/planGeneration';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiDownload, FiPrinter, FiRefreshCw, FiStar, FiCalendar, FiTarget, FiUsers, FiCheckCircle, FiShoppingBag, FiExternalLink } = FiIcons;

function PlanOutput() {
  const navigate = useNavigate();
  const { state, dispatch } = usePlanner();
  const [weeklySchedule, setWeeklySchedule] = useState([]);
  const [nextSteps, setNextSteps] = useState([]);

  useEffect(() => {
    if (!state.groups.length) {
      navigate('/groups');
      return;
    }

    const schedule = generateWeeklySchedule(state.groups, state.groupSettings);
    const steps = generateNextSteps();
    setWeeklySchedule(schedule);
    setNextSteps(steps);
  }, [state.groups, state.groupSettings, navigate]);

  const printPlan = () => {
    window.print();
  };

  const startOver = () => {
    dispatch({ type: 'RESET' });
    navigate('/');
  };

  const openShop = () => {
    window.open("https://shop.thriveedservices.com/", "_blank");
  };

  // Function to get suggested products for each skill
  const getSuggestedProducts = (skillFocus) => {
    const products = [];

    if (skillFocus.includes('Letter Names') || skillFocus.includes('Consonant sounds') || skillFocus.includes('Long vowel sounds') || skillFocus.includes('Short vowel sounds') || skillFocus.includes('Letter Sounds')) {
      products.push({ name: 'Alphabet Posters', url: 'https://shop.thriveedservices.com/products/alphabet-posters-ufli' });
      products.push({ name: 'Sound Wall', url: 'https://shop.thriveedservices.com/products/sound-wall-bundle' });
    }

    if (skillFocus.includes('Short vowels in CVC words') || skillFocus.includes('VC & CVC')) {
      products.push({ name: 'CVC Centers Bundle', url: 'https://shop.thriveedservices.com/products/cvc-short-vowel-bundle' });
    }

    if (skillFocus.includes('Consonant blends with short vowels') || skillFocus.includes('CVCC & CCVC')) {
      products.push({ name: 'CVC & Consonant Blends Decodable Stories', url: 'https://shop.thriveedservices.com/products/cvc-cvcc-ccvc-short-vowel-decodable-passages?_pos=8&_sid=da5b916cb&_ss=r' });
    }

    if (skillFocus.includes('Short vowels, digraphs') || skillFocus.includes('-tch') || skillFocus.includes('Consonant Digraphs')) {
      products.push({ name: 'H Brothers Digraphs Activities', url: 'https://shop.thriveedservices.com/products/digraphs-h-brothers-digraphs-worksheets-activities?_pos=2&_sid=3c8ceb713&_ss=r' });
    }

    if (skillFocus.includes('R-controlled vowels') || skillFocus.includes('R-Controlled')) {
      products.push({ name: 'R-Controlled Vowels Activities', url: 'https://shop.thriveedservices.com/products/r-controlled-vowels-bundle' });
    }

    if (skillFocus.includes('Long vowel spellings') || skillFocus.includes('Variant vowels') || skillFocus.includes('Low frequency') || skillFocus.includes('CVCe') || skillFocus.includes('Vowel Digraphs')) {
      products.push({ name: 'Long Vowels Word Work Bundle', url: 'https://shop.thriveedservices.com/products/long-vowel-words-worksheets-activities-printable-digital-growing-bundle' });
    }

    if (skillFocus.includes('Multisyllabic') || skillFocus.includes('2-Syllable') || skillFocus.includes('3-Syllable') || skillFocus.includes('4-Syllable')) {
      products.push({ name: 'Syllable Division Bundle', url: 'https://shop.thriveedservices.com/products/syllable-division-worksheets-bundle' });
      products.push({ name: 'Multisyllabic Words Practice', url: 'https://shop.thriveedservices.com/products/multisyllabic-words-practice-printable-games-editable-worksheets' });
    }

    return products;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-blue-50/20 to-purple-50/10">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/80 backdrop-blur-sm shadow-sm border-b print:hidden"
      >
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/groups')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              Back to Groups
            </button>
            <h1 className="text-xl font-semibold text-thrive-slate font-display">Your Teaching Plan</h1>
            <div className="flex items-center gap-2">
              <button
                onClick={printPlan}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <SafeIcon icon={FiPrinter} />
                Print
              </button>
              <button
                onClick={startOver}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <SafeIcon icon={FiRefreshCw} />
                Start Over
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <main className="max-w-6xl mx-auto px-4 py-8 print:p-4">
        {/* Success Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-thrive-peach to-thrive-teal text-white rounded-xl p-6 mb-8 print:hidden shadow-lg"
        >
          <div className="flex items-center gap-3 mb-2">
            <SafeIcon icon={FiStar} className="text-2xl" />
            <h2 className="text-2xl font-bold font-display">✨ Your Skill-Based Small Group Plan is Ready!</h2>
          </div>
          <p className="text-lg opacity-90">
            Based on {state.assessmentType} data for {state.students.length} students, organized into {state.groups.length} targeted groups.
          </p>
        </motion.div>

        {/* Groups Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 p-6 mb-8"
        >
          <h3 className="text-xl font-bold text-thrive-slate mb-4 flex items-center gap-2 font-display">
            <SafeIcon icon={FiUsers} className="text-thrive-teal" />
            📋 Groups Table
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-thrive-slate">Group #</th>
                  <th className="text-left py-3 px-4 font-semibold text-thrive-slate">Students</th>
                  <th className="text-left py-3 px-4 font-semibold text-thrive-slate">Skill Focus</th>
                  <th className="text-left py-3 px-4 font-semibold text-thrive-slate">Assessment & Score</th>
                  <th className="text-left py-3 px-4 font-semibold text-thrive-slate">Scope & Sequence Reference</th>
                </tr>
              </thead>
              <tbody>
                {state.groups.map((group, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 font-medium">Group {index + 1}</td>
                    <td className="py-3 px-4">
                      {group.students.map(s => s.name).join(', ')}
                    </td>
                    <td className="py-3 px-4 font-medium text-thrive-rose">{group.skillFocus}</td>
                    <td className="py-3 px-4 text-gray-600">
                      {group.students[0]?.assessmentDetail || `${state.assessmentType} scores`}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{group.scopeReference}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* This Week's Focus */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 p-6 mb-8"
        >
          <h3 className="text-xl font-bold text-thrive-slate mb-4 flex items-center gap-2 font-display">
            <SafeIcon icon={FiTarget} className="text-thrive-rose" />
            🎯 This Week's Focus
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {state.groups.map((group, index) => {
              const suggestedProducts = getSuggestedProducts(group.skillFocus);
              return (
                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-thrive-peach/50 transition-all bg-white/50">
                  <h4 className="font-semibold text-thrive-slate mb-3">Group {index + 1}: {group.skillFocus}</h4>
                  <ul className="space-y-2 mb-4">
                    {group.activities.map((activity, actIndex) => (
                      <li key={actIndex} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-thrive-teal mt-1">•</span>
                        {activity}
                      </li>
                    ))}
                  </ul>
                  {/* Suggested Products */}
                  {suggestedProducts.length > 0 && (
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-sm font-medium text-thrive-slate mb-2">Suggested Resources:</p>
                      <div className="flex flex-wrap gap-2">
                        {suggestedProducts.map((product, productIndex) => (
                          <a
                            key={productIndex}
                            href={product.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-thrive-teal to-thrive-slate hover:from-thrive-slate hover:to-thrive-teal rounded-full transition-all duration-200 hover:shadow-md"
                          >
                            {product.name}
                            <SafeIcon icon={FiExternalLink} className="text-xs" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Weekly Schedule */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 p-6 mb-8"
        >
          <h3 className="text-xl font-bold text-thrive-slate mb-4 flex items-center gap-2 font-display">
            <SafeIcon icon={FiCalendar} className="text-thrive-teal" />
            📅 Suggested Weekly Schedule
          </h3>
          <div className="grid grid-cols-5 gap-4">
            {weeklySchedule.map((day, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3 hover:border-thrive-mint transition-all bg-white/50">
                <h4 className="font-semibold text-thrive-slate mb-2 text-center">{day.day}</h4>
                <div className="space-y-2">
                  {day.sessions.map((session, sessionIndex) => (
                    <div key={sessionIndex} className="text-xs bg-gray-50/70 p-2 rounded">
                      <div className="text-gray-600">{session.group}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-thrive-mint/20 rounded-lg">
            <p className="text-sm text-thrive-slate">
              <strong>Sessions:</strong> {state.groupSettings.sessionMinutes} minutes each • <strong>Total Groups:</strong> {state.groups.length}
            </p>
          </div>
        </motion.div>

        {/* Shop Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-thrive-peach p-6 mb-8 print:hidden"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold text-thrive-slate mb-2 font-display">Need Science of Reading Resources?</h3>
              <p className="text-gray-600">
                Find evidence-based activities, decodable texts, and assessment tools for your small groups.
              </p>
            </div>
            <button
              onClick={openShop}
              className="bg-thrive-peach hover:bg-thrive-rose text-thrive-slate px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
            >
              <SafeIcon icon={FiShoppingBag} />
              Visit Our Shop
            </button>
          </div>
        </motion.div>

        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 p-6 mb-8"
        >
          <h3 className="text-xl font-bold text-thrive-slate mb-4 flex items-center gap-2 font-display">
            <SafeIcon icon={FiCheckCircle} className="text-thrive-peach" />
            💡 Next Steps
          </h3>
          <ol className="space-y-3">
            {nextSteps.map((step, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="bg-thrive-peach/20 text-thrive-slate w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold mt-0.5">
                  {index + 1}
                </span>
                <span className="text-gray-700">{step}</span>
              </li>
            ))}
          </ol>
        </motion.div>

        {/* Encouragement Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-r from-thrive-teal to-thrive-slate text-white rounded-xl p-6 text-center print:hidden shadow-lg"
        >
          <SafeIcon icon={FiStar} className="text-3xl mx-auto mb-3" />
          <h3 className="text-xl font-bold mb-2 font-display">Remember: Groups are Flexible!</h3>
          <p className="text-lg opacity-90 mb-4">
            Update groups as soon as students master their current skill. You're doing amazing work! 🌟
          </p>
          <button
            onClick={startOver}
            className="bg-white text-thrive-teal px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors shadow-md"
          >
            Create Another Plan
          </button>
        </motion.div>
      </main>
    </div>
  );
}

export default PlanOutput;